import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.js`;

const Section = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.02);
  font-size: 11px;
  font-weight: 700;
  color: #444;
  cursor: pointer;
  flex-shrink: 0;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 6px;
  padding: 6px 8px;
  align-items: center;
  flex-shrink: 0;
`;

const Search = styled.input`
  width: 52px;
  flex-shrink: 0;
  height: 26px;
  padding: 0 6px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 11px;
`;

const ZoomLabel = styled.span`
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SizeSlider = styled.input`
  flex: 1;
  min-width: 48px;
`;

const Grid = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 8px 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${(p) => p.$thumb}px, 1fr));
  gap: 6px;
  align-content: start;
`;

const ThumbCard = styled.button`
  border: 2px solid ${(p) => (p.$active ? '#e97024' : 'rgba(0,0,0,0.08)')};
  border-radius: 8px;
  padding: 5px;
  background: ${(p) => (p.$active ? 'rgba(233,112,36,0.06)' : '#fff')};
  cursor: pointer;
  text-align: center;

  &:hover {
    border-color: #e97024;
  }
`;

const ThumbCanvas = styled.canvas`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
  background: #f3f4f6;
`;

const ThumbLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #555;
  margin-top: 4px;
`;

const EmptyHint = styled.div`
  padding: 12px 16px;
  font-size: 12px;
  color: #6b7280;
`;

const _thumbQueue = { running: 0, max: 3, waiters: [] };
function acquireThumbSlot() {
  if (_thumbQueue.running < _thumbQueue.max) {
    _thumbQueue.running += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => { _thumbQueue.waiters.push(resolve); });
}
function releaseThumbSlot() {
  const next = _thumbQueue.waiters.shift();
  if (next) next();
  else _thumbQueue.running -= 1;
}

function PageThumbnail({ pdfDoc, pageNum, width, active, onClick, labelCount }) {
  const canvasRef = useRef(null);
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !pdfDoc || !canvasRef.current) return undefined;
    let cancelled = false;
    let renderTask;

    (async () => {
      await acquireThumbSlot();
      if (cancelled) { releaseThumbSlot(); return; }
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;
        const vp = page.getViewport({ scale: 1 });
        const scale = width / vp.width;
        const scaled = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        canvas.width = scaled.width;
        canvas.height = scaled.height;
        renderTask = page.render({ canvasContext: ctx, viewport: scaled });
        await renderTask.promise;
      } catch (_) {
        // ignore cancel
      } finally {
        releaseThumbSlot();
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel?.();
    };
  }, [visible, pdfDoc, pageNum, width]);

  return (
    <ThumbCard ref={cardRef} type="button" $active={active} onClick={onClick}>
      <ThumbCanvas ref={canvasRef} />
      <ThumbLabel>
        Page {pageNum}
        {labelCount > 0 ? ` · ${labelCount}` : ''}
      </ThumbLabel>
    </ThumbCard>
  );
}

function useDebouncedValue(value, delayMs = 150) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function SheetPagesPanel({
  activeSheet,
  activePage,
  pdfUrl,
  pdfDoc: pdfDocProp,
  onSelectPage,
}) {
  const [pageSearch, setPageSearch] = useState('');
  const [thumbSize, setThumbSize] = useState(100);
  const [pagesOpen, setPagesOpen] = useState(true);
  const [pdfDocOwned, setPdfDocOwned] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const debouncedThumbSize = useDebouncedValue(thumbSize);

  const pdfDoc = pdfDocProp || pdfDocOwned;
  const pageCount = activeSheet?.pageCount || numPages || 0;

  useEffect(() => {
    if (pdfDocProp) {
      setNumPages(pdfDocProp.numPages);
      return undefined;
    }
    if (!pdfUrl) {
      setPdfDocOwned(null);
      setNumPages(0);
      return undefined;
    }
    let cancelled = false;
    pdfjsLib.getDocument({
      url: pdfUrl,
      rangeChunkSize: 65536,
      disableAutoFetch: true,
      disableStream: false,
    }).promise
      .then((doc) => {
        if (!cancelled) {
          setPdfDocOwned(doc);
          setNumPages(doc.numPages);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPdfDocOwned(null);
          setNumPages(0);
        }
      });
    return () => { cancelled = true; };
  }, [pdfUrl, pdfDocProp]);

  const labelsPerPage = useMemo(() => {
    const map = new Map();
    const comps = activeSheet?.ir?.components || [];
    for (const c of comps) {
      const p = (c.pageIndex ?? 0) + 1;
      map.set(p, (map.get(p) || 0) + 1);
    }
    return map;
  }, [activeSheet?.ir?.components]);

  const pages = useMemo(() => {
    const maxPage = Math.max(pageCount, numPages, 0);
    const q = pageSearch.trim().toLowerCase();
    return Array.from({ length: maxPage }, (_, i) => i + 1).filter((p) => {
      if (!q) return true;
      return String(p).includes(q) || `page ${p}`.includes(q);
    });
  }, [pageCount, numPages, pageSearch]);

  const activePageRef = useRef(null);
  useEffect(() => {
    if (activePageRef.current) {
      activePageRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activePage]);

  if (!activeSheet) {
    return <EmptyHint>Select a sheet to browse pages.</EmptyHint>;
  }

  const totalPages = pageCount || numPages || 0;

  return (
    <Section>
      <SectionHeader type="button" onClick={() => setPagesOpen((o) => !o)}>
        <span>{totalPages ? `${totalPages} pages` : 'Pages'}</span>
        <span>{pagesOpen ? '▾' : '▸'}</span>
      </SectionHeader>
      {pagesOpen && (
        <>
          <Toolbar>
            <Search
              placeholder="#"
              title="Jump to page number"
              value={pageSearch}
              onChange={(e) => setPageSearch(e.target.value)}
            />
            <ZoomLabel>Thumb</ZoomLabel>
            <SizeSlider
              type="range"
              min={72}
              max={160}
              value={thumbSize}
              onChange={(e) => setThumbSize(Number(e.target.value))}
            />
          </Toolbar>
          <Grid $thumb={debouncedThumbSize}>
            {!pdfDoc && <EmptyHint>Loading PDF preview…</EmptyHint>}
            {pdfDoc && pages.map((pageNum) => (
              <div
                key={pageNum}
                ref={activePage === pageNum ? activePageRef : null}
              >
                <PageThumbnail
                  pdfDoc={pdfDoc}
                  pageNum={pageNum}
                  width={debouncedThumbSize}
                  active={activePage === pageNum}
                  labelCount={labelsPerPage.get(pageNum) || 0}
                  onClick={() => onSelectPage(pageNum)}
                />
              </div>
            ))}
          </Grid>
        </>
      )}
    </Section>
  );
}
